import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    TextField,
    InputAdornment,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    FormControl,
    Select,
    MenuItem,
    CircularProgress,
    Alert,
    Chip,
    Pagination,
    Stack,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Button,
    Tooltip
} from '@mui/material';
import { Search, Eye, SquarePen, ArrowLeft } from 'lucide-react';
import { getRefundDetails, updateRefundDetails } from '../../Services/Bookings/BookingsApis';
import { useNavigate } from 'react-router-dom';
import { useGlobalSnackbar } from '../../Components/Common/GlobalSanckbar';
import PermissionWrapper from '../../Components/Auth/PermissionWrapper';
import GlobalPagination from '../../Components/Common/Globalpagination';

const getStatusChipSx = (status) => {
    switch (status?.toUpperCase()) {
        case 'COMPLETED':
        case 'APPROVED':
            return { bg: '#E8F5E9', color: '#2E7D32', border: '#C8E6C9' };
        case 'REQUESTED':
            return { bg: '#FFF3E0', color: '#E65100', border: '#FFE0B2' };
        case 'PROCESSING':
            return { bg: '#E3F2FD', color: '#1565C0', border: '#BBDEFB' };
        case 'REJECTED':
        case 'FAILED':
            return { bg: '#FFEBEE', color: '#C62828', border: '#FFCDD2' };
        default:
            return { bg: '#F5F5F5', color: '#616161', border: '#E0E0E0' };
    }
};

const inputStyleSx = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '6px',
        fontSize: '0.85rem',
        bgcolor: '#FFFFFF',
        '& fieldset': { borderColor: '#E0E0E0' },
        '&:hover fieldset': { borderColor: '#BDBDBD' },
        '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
    }
};

const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
    });
};

// Validation function to allow only letters, numbers, and spaces (no special characters)
const validateGatewayRefundId = (value) => {
    // Allow only letters, numbers, and spaces - remove special characters
    const cleaned = value.replace(/[^a-zA-Z0-9\s]/g, '');
    // Remove multiple spaces between words, but keep leading/trailing spaces during typing
    const singleSpaced = cleaned.replace(/\s+/g, ' ');
    return singleSpaced;
};

const RefundRequests = () => {
    const navigate = useNavigate();
    const { showSnackbar } = useGlobalSnackbar();

    const [refundList, setRefundList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [filters, setFilters] = useState({
        search: '',
        status: '',
        page: 1,
        limit: 25,
    });

    const [pagination, setPagination] = useState({
        total: 0,
        page: 1,
        limit: 25,
        totalPages: 1,
    });
    const [statusCounts, setStatusCounts] = useState({
        requested: 0,
        processing: 0,
        approved: 0,
        completed: 0,
        rejected: 0
    });

    // Update status modal
    const [selectedRefund, setSelectedRefund] = useState(null);
    const [updateModalOpen, setUpdateModalOpen] = useState(false);
    const [updateFormData, setUpdateFormData] = useState({
        status: 'COMPLETED',
        adminRemarks: '',
        gatewayRefundId: ''
    });
    const [updating, setUpdating] = useState(false);

    const fetchRefunds = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await getRefundDetails(filters);
            if (response && response.success) {
                setRefundList(response.data || []);
                if (response.statusCounts) {
                    setStatusCounts(response.statusCounts);
                }
                if (response.pagination) {
                    setPagination({
                        total: response.pagination.total || response.data?.length || 0,
                        page: response.pagination.page || 1,
                        limit: response.pagination.limit || 10,
                        totalPages: response.pagination.totalPages || 1,
                    });
                }
            } else if (Array.isArray(response)) {
                setRefundList(response);
            }
        } catch (err) {
            console.error("Error fetching refund details:", err);
            setError(err.response?.data?.message || err.message || 'Failed to fetch refund details');
            setRefundList([]);
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            fetchRefunds();
        }, 300);
        return () => clearTimeout(timeoutId);
    }, [fetchRefunds]);

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value,
            page: field === 'page' ? value : 1,
        }));
    };

    const handleOpenViewModal = (refund) => {
        navigate('/bookings/refund-request-details', { state: { refundId: refund.id } });
    };

    const handleOpenUpdateModal = (refund) => {
        setSelectedRefund(refund);
        setUpdateFormData({
            status: refund.status || 'COMPLETED',
            adminRemarks: refund.adminRemarks || '',
            gatewayRefundId: refund.gatewayRefundId || ''
        });
        setUpdateModalOpen(true);
    };

    const handleCloseUpdateModal = () => {
        setUpdateModalOpen(false);
        setSelectedRefund(null);
        setUpdateFormData({
            status: 'COMPLETED',
            adminRemarks: '',
            gatewayRefundId: ''
        });
    };

    const handleUpdateSubmit = async () => {
        if (!selectedRefund) return;

        if (!updateFormData.status) {
            showSnackbar("Status is required", "error");
            return;
        }

        try {
            setUpdating(true);
            const payload = {
                status: updateFormData.status,
                adminRemarks: updateFormData.adminRemarks || null,
                gatewayRefundId: updateFormData.gatewayRefundId || null
            };

            const response = await updateRefundDetails(selectedRefund.id, payload);
            if (response && (response.success || response.data)) {
                showSnackbar("Refund status updated successfully!", "success");
                handleCloseUpdateModal();
                fetchRefunds();
            } else {
                showSnackbar(response?.message || "Failed to update refund status", "error");
            }
        } catch (err) {
            console.error("Error updating refund details:", err);
            const errMsg = err.response?.data?.message || err.message || "Failed to update refund status";
            showSnackbar(errMsg, "error");
        } finally {
            setUpdating(false);
        }
    };

    return (
        <PermissionWrapper permission="view">
            <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#FBFBFB', minHeight: '100vh' }}>
                {/* Header Section */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 2 }}>
                    <Typography sx={{ color: '#222', fontSize: { xs: '1.75rem', md: '1.8rem' }, fontWeight: 500 }}>
                        Refund Requests
                    </Typography>
                    <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                        <TextField
                            placeholder="Search Refund ID / Booking ID"
                            variant="outlined" size="small" value={filters.search}
                            onChange={(e) => handleFilterChange('search', e.target.value)}
                            sx={{ width: { xs: '100%', sm: 260 }, ...inputStyleSx }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search size={16} color="#999" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <FormControl size="small" sx={{ minWidth: 150, ...inputStyleSx }}>
                            <Select
                                displayEmpty
                                value={filters.status}
                                onChange={(e) => handleFilterChange('status', e.target.value)}
                                sx={{ borderRadius: '6px', fontSize: '0.85rem' }}
                            >
                                <MenuItem value="" sx={{ fontSize: '0.85rem' }}>All Statuses</MenuItem>
                                <MenuItem value="REQUESTED" sx={{ fontSize: '0.85rem' }}>Requested</MenuItem>
                                <MenuItem value="PROCESSING" sx={{ fontSize: '0.85rem' }}>Processing</MenuItem>
                                <MenuItem value="COMPLETED" sx={{ fontSize: '0.85rem' }}>Completed</MenuItem>
                                <MenuItem value="APPROVED" sx={{ fontSize: '0.85rem' }}>Approved</MenuItem>
                                <MenuItem value="REJECTED" sx={{ fontSize: '0.85rem' }}>Rejected</MenuItem>
                            </Select>
                        </FormControl>
                    </Stack>
                </Box>

                {/* Status Summary Cards (Side by Side) */}
                <Box sx={{ display: 'flex', gap: 2, mb: 4, flexWrap: 'wrap' }}>
                    {[
                        { label: 'All Refunds', value: pagination.total || 0, status: '', color: '#64748B', activeBg: '#F8FAFC', activeBorder: '#64748B' },
                        { label: 'Requested', value: statusCounts.requested || 0, status: 'REQUESTED', color: '#E65100', activeBg: '#FFF3E0', activeBorder: '#E65100' },
                        { label: 'Processing', value: statusCounts.processing || 0, status: 'PROCESSING', color: '#1565C0', activeBg: '#E3F2FD', activeBorder: '#1565C0' },
                        { label: 'Approved', value: statusCounts.approved || 0, status: 'APPROVED', color: '#2E7D32', activeBg: '#E8F5E9', activeBorder: '#2E7D32' },
                        { label: 'Completed', value: statusCounts.completed || 0, status: 'COMPLETED', color: '#2E7D32', activeBg: '#E8F5E9', activeBorder: '#2E7D32' },
                        { label: 'Rejected', value: statusCounts.rejected || 0, status: 'REJECTED', color: '#C62828', activeBg: '#FFEBEE', activeBorder: '#C62828' }
                    ].map((card) => {
                        const isActive = filters.status === card.status;
                        return (
                            <Paper
                                key={card.label} elevation={0}
                                onClick={() => handleFilterChange('status', card.status)}
                                sx={{
                                    flex: '1 1 120px', minWidth: '120px', p: 1, borderRadius: '5px',
                                    bgcolor: isActive ? card.activeBg : '#FFFFFF', cursor: 'pointer',
                                    border: isActive ? `1px solid ${card.activeBorder}` : '1px solid #E2E8F0',

                                }}
                            >
                                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: card.color, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                    {card.label}
                                </Typography>
                                <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: '#0F172A', mt: 0.5 }}>
                                    {card.value}
                                </Typography>
                            </Paper>
                        );
                    })}
                </Box>

                {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

                {/* Table Section */}
                <Paper sx={{ borderRadius: "8px", border: "1px solid #E8E8E8", overflow: "hidden" }} elevation={0}>
                    <TableContainer>
                        <Table sx={{ width: '100%', minWidth: 1200 }}>
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#F8FAFC', borderBottom: '2px solid #EEF2F6' }}>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap' }}>
                                        Refund ID
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap' }}>
                                        Booking ID
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap' }}>
                                        Refund Amount
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap' }}>
                                        Cancellation Fee
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap' }}>
                                        Status
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap' }}>
                                        Gateway
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '130px' }}>
                                        Created By
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '130px' }}>
                                        Updated By
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap' }}>
                                        Actions
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                                            <CircularProgress size={24} sx={{ color: '#FFBD65' }} />
                                        </TableCell>
                                    </TableRow>
                                ) : refundList.length > 0 ? (
                                    refundList.map((item, idx) => {
                                        const chipStyle = getStatusChipSx(item.status);
                                        const bookingDisplay = item.booking?.bookingId || `#${item.bookingId}`;

                                        return (
                                            <TableRow key={item.id} sx={{
                                                transition: 'background-color 0.2s',
                                                '& td': { borderBottom: '1px solid #F0F0F0' },
                                                '&:last-child td': { borderBottom: 0 }
                                            }}>
                                                <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.75rem', fontWeight: 500, color: '#1E293B', whiteSpace: 'nowrap' }}>
                                                    {item.refundId}
                                                </TableCell>
                                                <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.75rem' }}>
                                                    <Typography
                                                        onClick={() => navigate('/bookings/view-details', { state: { booking: item.bookingId } })}
                                                        sx={{ fontSize: '0.75rem', fontWeight: 500, color: '#1565C0', cursor: 'pointer', '&:hover': { textDecoration: 'underline' }, whiteSpace: 'nowrap' }}
                                                    >
                                                        {bookingDisplay}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Typography sx={{ color: '#2E7D32', fontSize: '0.75rem', fontWeight: 600 }}>
                                                            {item.currency || 'INR'} {item.refundAmount}
                                                        </Typography>
                                                        {item.refundPercentage !== undefined && (
                                                            <Chip
                                                                label={`${item.refundPercentage}%`}
                                                                size="small"
                                                                sx={{
                                                                    bgcolor: '#E8F5E9',
                                                                    color: '#2E7D32',
                                                                    border: '1px solid #C8E6C9',
                                                                    fontWeight: 500,
                                                                    fontSize: '0.68rem',
                                                                    borderRadius: '4px',
                                                                    height: '18px'
                                                                }}
                                                            />
                                                        )}
                                                    </Box>
                                                </TableCell>
                                                <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.75rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                    {item.currency || 'INR'} {item.cancellationFee}
                                                </TableCell>
                                                <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                                                    <Chip
                                                        label={item.status}
                                                        size="small"
                                                        sx={{
                                                            bgcolor: chipStyle.bg,
                                                            color: chipStyle.color,
                                                            border: `1px solid ${chipStyle.border}`,
                                                            fontWeight: 500,
                                                            fontSize: '0.7rem',
                                                            borderRadius: '4px',
                                                            height: '22px'
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.75rem', color: '#334155', textTransform: 'capitalize', whiteSpace: 'nowrap' }}>
                                                    {item.paymentGateway || 'Razorpay'}
                                                </TableCell>
                                                <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.75rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                    {item.cuName || '-'}
                                                </TableCell>
                                                <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.75rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                    {item.uuName || '-'}
                                                </TableCell>
                                                <TableCell align="right" sx={{ py: 1.25, px: 1.5 }}>
                                                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                                        <Tooltip title="View Details">
                                                            <IconButton
                                                                disableFocusRipple
                                                                disableRipple
                                                                disableTouchRipple
                                                                size="small"
                                                                sx={{ color: '#FFBD65' }}
                                                                onClick={() => handleOpenViewModal(item)}
                                                            >
                                                                <Eye size={16} />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <PermissionWrapper permission="edit" mode="disable">
                                                            <Tooltip title="Update Status">
                                                                <IconButton
                                                                    disableFocusRipple
                                                                    disableRipple
                                                                    disableTouchRipple
                                                                    size="small"
                                                                    sx={{ color: '#FFBD65' }}
                                                                    onClick={() => handleOpenUpdateModal(item)}
                                                                >
                                                                    <SquarePen size={16} />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </PermissionWrapper>
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={9} align="center" sx={{ py: 4, color: '#AAA', fontSize: '0.8rem' }}>
                                            No refund requests found.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>

                {/* Pagination */}
                <GlobalPagination
                    page={filters.page}
                    pageSize={filters.limit}
                    totalRecords={pagination.total}
                    totalPages={pagination.totalPages}
                    onPageChange={(newPage) => {
                        handleFilterChange('page', newPage);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    onPageSizeChange={(newSize) => {
                        handleFilterChange('limit', newSize);
                    }}
                    loading={loading}
                />

                {/* Update Status Modal */}
                <Dialog open={updateModalOpen} onClose={handleCloseUpdateModal} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '6px' } }}>
                    <DialogTitle sx={{ fontWeight: 600, color: '#222', fontSize: '1.1rem', pb: 1 }}>
                        Update Refund Status
                    </DialogTitle>
                    <DialogContent>
                        <Stack spacing={2} sx={{ mt: 2 }}>
                            <Box>
                                <Typography sx={{ mb: 1, fontWeight: 550, color: '#333', fontSize: '0.85rem' }}>
                                    Status *
                                </Typography>
                                <FormControl fullWidth size="small" sx={inputStyleSx}>
                                    <Select
                                        value={updateFormData.status}
                                        onChange={(e) => setUpdateFormData(prev => ({ ...prev, status: e.target.value }))}
                                        sx={{ borderRadius: '6px', fontSize: '0.85rem' }}
                                    >
                                        <MenuItem value="REQUESTED" sx={{ fontSize: '0.85rem' }}>REQUESTED</MenuItem>
                                        <MenuItem value="PROCESSING" sx={{ fontSize: '0.85rem' }}>PROCESSING</MenuItem>
                                        <MenuItem value="COMPLETED" sx={{ fontSize: '0.85rem' }}>COMPLETED</MenuItem>
                                        <MenuItem value="APPROVED" sx={{ fontSize: '0.85rem' }}>APPROVED</MenuItem>
                                        <MenuItem value="REJECTED" sx={{ fontSize: '0.85rem' }}>REJECTED</MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>

                            <Box>
                                <Typography sx={{ mb: 1, fontWeight: 550, color: '#333', fontSize: '0.85rem' }}>
                                    Gateway Refund ID
                                </Typography>
                                <TextField
                                    fullWidth
                                    size="small"
                                    value={updateFormData.gatewayRefundId}
                                    onChange={(e) => setUpdateFormData(prev => ({ ...prev, gatewayRefundId: validateGatewayRefundId(e.target.value) }))}
                                    placeholder="e.g. rfnd_P123456789"
                                    sx={inputStyleSx}
                                />
                            </Box>

                            <Box>
                                <Typography sx={{ mb: 1, fontWeight: 550, color: '#333', fontSize: '0.85rem' }}>
                                    Admin Remarks
                                </Typography>
                                <TextField
                                    fullWidth
                                    size="small"
                                    multiline
                                    rows={3}
                                    value={updateFormData.adminRemarks}
                                    onChange={(e) => setUpdateFormData(prev => ({ ...prev, adminRemarks: e.target.value }))}
                                    placeholder="e.g. Refund processed via Razorpay dashboard"
                                    sx={inputStyleSx}
                                />
                            </Box>
                        </Stack>
                    </DialogContent>
                    <DialogActions sx={{ p: 2.5, pt: 1 }}>
                        <Button
                            disableElevation
                            disableFocusRipple
                            disableRipple
                            disableTouchRipple
                            onClick={handleCloseUpdateModal}
                            sx={{
                                borderRadius: '6px',
                                textTransform: 'none',
                                color: '#222',
                                px: 2.5,
                                fontSize: '0.85rem'
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            disableElevation
                            disableFocusRipple
                            disableTouchRipple
                            onClick={handleUpdateSubmit}
                            disabled={updating}
                            variant="contained"
                            sx={{
                                bgcolor: '#FFBD65',
                                borderRadius: '6px',
                                textTransform: 'none',
                                px: 3,
                                color: '#fff',
                                fontSize: '0.85rem'
                            }}
                        >
                            {updating ? <CircularProgress size={20} color="inherit" /> : 'Update Refund'}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </PermissionWrapper>
    );
};

export default RefundRequests;
